import { useLocation, useNavigate, Link } from "react-router-dom";
import { Phone, Star, MapPin, Clock, Mail } from "lucide-react";
import { scrollToId, PHONE, PHONE_TEL, SERVICE_AREAS, GOOGLE_MAPS_URL, CITY_LINKS } from "@/lib/site";
import { ContactForm } from "@/components/landing/ContactForm";

const LINKS = [
  { label: "Fall Special", id: "fall-special" },
  { label: "Services", id: "services" },
  { label: "How It Works", id: "how-it-works" },
  { label: "Why Us", id: "why-us" },
  { label: "Coverage", id: "coverage" },
  { label: "Book Pickup", id: "book" },
];

export const Footer = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const goSection = (id) => {
    if (location.pathname !== "/") navigate("/", { state: { scrollTo: id } });
    else scrollToId(id);
  };

  return (
    <footer data-testid="footer" className="grain relative bg-brand-navy pb-28 pt-16 text-white md:pb-16">
      <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6">
        <ContactForm />
        <div className="grid gap-10 md:grid-cols-2 lg:grid-cols-4">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-fall-amber">
                <Star className="h-5 w-5 fill-brand-navy text-brand-navy" />
              </span>
              <span className="font-display text-lg font-extrabold tracking-tight">Oxford Cleaners</span>
            </div>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-slate-400">
              Veteran &amp; family-owned dry cleaning with free pickup &amp; delivery across the East Valley.
            </p>
            <a
              data-testid="footer-phone-link"
              href={PHONE_TEL}
              className="mt-5 inline-flex items-center gap-2 rounded-full bg-fall-amber px-5 py-2.5 text-sm font-bold text-brand-navy transition-transform hover:scale-105"
            >
              <Phone className="h-4 w-4" /> {PHONE}
            </a>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-300">Explore</p>
            <ul className="mt-4 space-y-2.5">
              {LINKS.map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`footer-link-${l.id}`}
                    onClick={() => goSection(l.id)}
                    className="text-sm font-semibold text-slate-300 transition-colors hover:text-fall-amber"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
              <li>
                <Link
                  data-testid="footer-link-wedding-dress"
                  to="/wedding-dress-cleaning-preservation"
                  className="text-sm font-semibold text-slate-300 transition-colors hover:text-fall-amber"
                >
                  Wedding Dress Cleaning &amp; Preservation
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-300">Areas we serve</p>
            <ul className="mt-4 space-y-2.5">
              {SERVICE_AREAS.map((city) => (
                <li key={city}>
                  <Link
                    data-testid={`footer-area-${city.toLowerCase().replace(/\s/g, "-")}`}
                    to={CITY_LINKS[city]}
                    className="flex items-center gap-2 text-sm font-semibold text-slate-300 transition-colors hover:text-fall-amber"
                  >
                    <MapPin className="h-3.5 w-3.5 text-fall-amber" />
                    {city}, AZ
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-[0.25em] text-amber-300">Visit &amp; hours</p>
            <img
              data-testid="footer-business-card"
              src="/images/oxford-card.png"
              alt="Oxford Cleaners business card — services, hours and contact details"
              className="mt-4 w-44 rounded-2xl border border-white/15 shadow-lg"
            />
            <p className="mt-4 flex items-start gap-2 text-sm text-slate-300">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-fall-amber" />
              5865 W Ray Rd, Suite 3, Chandler, AZ 85226
            </p>
            <p className="mt-3 flex items-start gap-2 text-sm text-slate-300">
              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-fall-amber" />
              Mon–Fri 7am–5pm · Sat 10am–2pm · Sun closed
            </p>
            <a
              data-testid="footer-email-link"
              href="mailto:oxfordcleanersaz@gmail.com"
              className="mt-3 flex items-start gap-2 text-sm text-slate-300 transition-colors hover:text-fall-amber"
            >
              <Mail className="mt-0.5 h-4 w-4 shrink-0 text-fall-amber" />
              oxfordcleanersaz@gmail.com
            </a>
            <a
              data-testid="footer-google-link"
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noreferrer"
              className="mt-3 flex items-start gap-2 text-sm text-slate-300 transition-colors hover:text-fall-amber"
            >
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-fall-amber" />
              Find us on Google Maps
            </a>
          </div>
        </div>

        <div className="mt-12 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-xs text-slate-500 sm:flex-row">
          <p>© 2026 Oxford Cleaners · oxford-cleaners.com</p>
          <p className="flex items-center gap-1.5 font-semibold text-slate-400">
            <Star className="h-3.5 w-3.5 fill-fall-amber text-fall-amber" /> Veteran &amp; Family-Owned
          </p>
        </div>
      </div>
    </footer>
  );
};
